import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import store from '@/redux/store';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@/styles/reset.scss';
import '@/styles/global.scss';
import Routes from '@/Routes';
import {
  constants_default_value,
  keiji_search_default_value,
  set_constants,
  set_keiji_search,
  set_prev_path,
  set_user_search,
  user_search_default_value,
} from '@/redux/cache';
import { getApps, FirebaseOptions, initializeApp } from 'firebase/app';
import {
  getDatabase,
  ref,
  onChildAdded,
  onChildChanged,
  DataSnapshot,
  remove,
} from 'firebase/database';
import {
  set_calls,
  set_loading,
  set_message_readed,
  set_message_update,
} from '@/redux/realtime';
import { useDispatch, useSelector } from 'react-redux';
import { go_api_v1 } from './plugins/axios';
import { User } from '@/types';
import { set_open_menus } from './redux/dom';
import { ToastContainer } from 'react-toastify';
import { set_affiliator } from './redux/auth';
import { createTheme } from '@mui/material';
import { ThemeProvider } from '@mui/system';
import AgeApprovalModal from './components/AgeApprovalModal';
import { setNewCount } from './redux/slices/newCount';
import { getNewCount } from './plugins/project';

const App = () => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();

  const [path, set_path] = useState('');
  useEffect(() => {
    // 以前のurlを保存
    window.scrollTo(0, 0);
    store.dispatch(set_prev_path(path));
    store.dispatch(set_loading([]));
    set_path(pathname);
  }, [path, pathname]);

  // firebase
  const auth_user = useSelector((state) => state.auth.user);
  useEffect(() => {
    const firebaseConfig: FirebaseOptions = {
      apiKey: 'AIzaSyD9a72qe3tsnELt2_41v3GbQT040411XOU',
      authDomain: 'papa-database.firebaseapp.com',
      databaseURL: 'https://papa-database.firebaseio.com',
      projectId: 'papa-database',
      storageBucket: 'papa-database.appspot.com',
      messagingSenderId: '605317768764',
      appId: '1:605317768764:web:2d181227332d55e7e88539',
      measurementId: 'G-G0FNJXXYK8',
    };
    if (getApps().length === 0) initializeApp(firebaseConfig);
    const db = getDatabase();

    dispatch(set_message_readed({}));
    dispatch(set_message_update({}));
    const update_store_from_data = (data: DataSnapshot) => {
      console.log(data.key + ':' + data.val());
      const key = data.key;
      if (key === null) return;
      if (key.indexOf('message_readed') !== -1) {
        const message_readed = store.getState().realtime.message_readed;
        const message_readed_copy = { ...message_readed };
        message_readed_copy[key.replace('message_readed_', '')] = data.val();
        dispatch(set_message_readed(message_readed_copy));
      }
      if (key.indexOf('message_update') !== -1) {
        const message_update = store.getState().realtime.message_update;
        const message_update_copy = { ...message_update };
        message_update_copy[key.replace('message_update_', '')] = data.val();
        dispatch(set_message_update(message_update_copy));
      }
      if (key.indexOf('partner_peer_id') !== -1) {
        const calls = store.getState().realtime.calls;
        const calls_copy = { ...calls };
        calls_copy[key.replace('partner_peer_id_', '')] = data.val();
        dispatch(set_calls(calls_copy));
      }
      remove(ref(db, `${auth_user?.id}/${key}`));
    };

    const my_ref = ref(db, `${auth_user?.id}`);
    onChildAdded(my_ref, update_store_from_data);
    onChildChanged(my_ref, update_store_from_data);
  }, [auth_user?.id, dispatch]);

  const body_on_click = (e: React.MouseEvent) => {
    if (
      (e.target as HTMLElement).closest('#header_side_button_menus') === null
    ) {
      dispatch(set_open_menus(false));
    }
  };

  // オブジェクト対応
  const user_search = useSelector((state) => state.cache.user_search);
  const keiji_search = useSelector((state) => state.cache.keiji_search);
  const constants = useSelector((state) => state.cache.constants);
  const calls = useSelector((state) => state.realtime.calls);
  const message_readed = useSelector((state) => state.realtime.message_readed);
  const message_update = useSelector((state) => state.realtime.message_update);
  const affiliator = useSelector((state) => state.auth.affiliator);
  useEffect(() => {
    if (user_search === undefined)
      dispatch(set_user_search(user_search_default_value));
    if (keiji_search === undefined)
      dispatch(set_keiji_search(keiji_search_default_value));
    if (constants === undefined)
      dispatch(set_constants(constants_default_value));
    if (calls === undefined) dispatch(set_calls({}));
    if (message_readed === undefined) dispatch(set_message_readed({}));
    if (message_update === undefined) dispatch(set_message_update({}));
    if (affiliator === undefined) dispatch(set_affiliator(null));
  }, [
    user_search,
    keiji_search,
    dispatch,
    constants,
    calls,
    message_readed,
    message_update,
    affiliator,
  ]);

  const theme = createTheme({
    status: {
      primary: '#c2a136',
    },
    typography: {
      fontFamily: 'Noto Sans JP',
      fontWeightRegular: 500,
      h1: {
        fontSize: 28,
        fontWeight: 'bold',
      },
      h2: {
        fontSize: 20,
        fontWeight: 'bold',
      },
      body2: {
        fontSize: 14,
      },
    },
  });

  // require login url
  const requireOpen =
    auth_user.plan === '' &&
    ['/home', '/keijis', '/calendar', '/mypage'].includes(pathname);
  const [open, setOpen] = useState(requireOpen);

  useEffect(() => {
    if (!requireOpen) return;
    const fetch = async () => {
      const res1 = await go_api_v1.get<User>('/user');
      const auth_user = res1.data;
      const requireOpen =
        auth_user.plan === '' &&
        ['/home', '/keijis', '/calendar', '/mypage'].includes(pathname);
      const res2 = await go_api_v1.get('/age_approval');
      setOpen(requireOpen && res2.data === null);
    };
    fetch();
  }, [requireOpen, pathname, dispatch]);

  useEffect(() => {
    if (Object.keys(auth_user).length === 0) return;
    const fetch = async () => {
      const data = await getNewCount();
      dispatch(setNewCount(data));
    };
    fetch();
  }, [auth_user, dispatch]);

  if (
    user_search !== undefined &&
    keiji_search !== undefined &&
    constants !== undefined
  ) {
    return (
      <div onClick={body_on_click}>
        <ToastContainer />
        <ThemeProvider theme={theme}>
          <Routes />
          <AgeApprovalModal open={open} setOpen={setOpen} />
        </ThemeProvider>
      </div>
    );
  } else {
    return <></>;
  }
};

export default App;
